import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/referral/me")
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !data) return null;

  const { code, share_url, count, credits_months, bonus_days_per_referral } = data;
  const bonusDays = bonus_days_per_referral || 30;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(share_url);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const shareNative = async () => {
    const text = `Lleva tus finanzas en una con Finly+. Usa mi código ${code} y obtén días extra de prueba: ${share_url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Finly+", text, url: share_url });
      } catch (_) {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  return (
    <section
      className="clay-card p-6 mb-10 fade-up overflow-hidden relative"
      data-testid="referral-card"
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, #00E590 0%, rgba(0, 229, 144, 0) 70%)",
        }}
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-6 relative">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#00E590]/15 flex-shrink-0">
            <Gift className="w-5 h-5 text-[#00C97D]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-[#F1F5F9] mb-1">
              Invita y gana meses gratis
            </h3>
            <p className="text-sm text-[#64748B] leading-relaxed">
              Cada amigo que pague su primer mes te suma{" "}
              <span className="font-medium text-[#F1F5F9]">
                {bonusDays} días extra
              </span>{" "}
              de Premium. Sin límite.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:flex-shrink-0">
          <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#1B2638] border border-[#2A3447]"
            data-testid="referral-code-box"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
              Tu código
            </span>
            <span
              className="font-numeric font-semibold text-[#F1F5F9] tracking-wider"
              data-testid="referral-code-value"
            >
              {code}
            </span>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={copyLink}
            className="h-11 px-4 border-[#2A3447] bg-[#131C2E] hover:bg-[#131C2E] text-[#F1F5F9] rounded-md font-medium"
            data-testid="referral-copy-button"
          >
            <Copy className="w-4 h-4 mr-2" /> Copiar enlace
          </Button>

          <Button
            type="button"
            onClick={shareNative}
            className="h-11 px-4 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-medium"
            data-testid="referral-share-button"
          >
            <Share2 className="w-4 h-4 mr-2" /> Compartir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-[#2A3447]">
        <div
          className="flex items-center gap-3"
          data-testid="referral-stat-count"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0052FF]/10">
            <Users className="w-4 h-4 text-[#0052FF]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
              Amigos suscritos
            </p>
            <p className="font-numeric font-semibold text-[#F1F5F9] text-lg">
              {count}
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-3"
          data-testid="referral-stat-credits"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#00E590]/15">
            <Gift className="w-4 h-4 text-[#00C97D]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
              Meses ganados
            </p>
            <p className="font-numeric font-semibold text-[#F1F5F9] text-lg">
              {credits_months}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
